import {Component, Input, OnInit, Optional} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {Subscription} from 'rxjs';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {BraintreeMatchedVccFilter} from '../../model/braintree-matching-vcc';
import {BraintreeMatchingVccService} from '../../service/braintree-matching-vcc.service';
import {DynamicAutocompleteItem} from '../../../shared/service/dynamic-autocomplete/dynamic-autocomplete.service';

@AutoUnsubscribe()
@Component({
    selector: 'braintree-matching-vcc-filter',
    templateUrl: './braintree-matching-vcc-filter.component.html',
    standalone: false
})
export class BraintreeMatchingVccFilterComponent implements OnInit {
  @Input() config!: BraintreeMatchedVccFilter;
  braintreeMatchingFilterForm!: FormGroup;

  adxTripReferenceControl = new FormControl<DynamicAutocompleteItem | null>(null);

  breakPoint$?: Subscription;
  isSmall: boolean = false;

  constructor(
    @Optional() public dialogRef: MatDialogRef<BraintreeMatchingVccFilterComponent>,
    private formBuilder: FormBuilder,
    private braintreeMatchingVccService: BraintreeMatchingVccService,
    breakpointObserver: BreakpointObserver,
  ) {
    this.braintreeMatchingFilterForm = this.formBuilder.group({
      releasedDateFrom: null,
      releasedDateTo: null,
      lastFourCC: null,
      adxTripReference: this.adxTripReferenceControl,
      isMatched: null,
    });

    this.breakPoint$ = breakpointObserver.observe([
      Breakpoints.Medium,
      Breakpoints.Small,
      Breakpoints.XSmall
    ]).subscribe((result: { matches: boolean; }) => {
      this.isSmall = result.matches;
    });
  }

  ngOnInit(): void {
    this.braintreeMatchingVccService.getBraintreeMatchingFilter().subscribe((filter: BraintreeMatchedVccFilter) => {
      this.config = filter;
      
      // Handle autocomplete field restoration
      if (filter && filter.adxTripReference && typeof filter.adxTripReference === 'string') {
        this.adxTripReferenceControl.setValue({
          value: filter.adxTripReference,
          originalValue: filter.adxTripReference
        });
        
        // Patch other fields but exclude the autocomplete field we just handled
        const { adxTripReference, ...otherFields } = filter;
        this.braintreeMatchingFilterForm.patchValue(otherFields);
      } else {
        this.braintreeMatchingFilterForm.patchValue(this.config || {});
      }
    });
  }

  applyBraintreeMatchingFilters() {
    const formValue = this.braintreeMatchingFilterForm.value;
    this.config = {
      ...formValue,
      releasedDateFrom: formValue.releasedDateFrom,
      releasedDateTo: formValue.releasedDateTo
    } as BraintreeMatchedVccFilter;

    if (typeof this.config.adxTripReference === 'object' && this.config.adxTripReference !== null) {
      this.config.adxTripReference = (this.config.adxTripReference as any).value || (this.config.adxTripReference as any).toString();
    }
    this.config.adxTripReference = this.config.adxTripReference?.trim();
    
    this.braintreeMatchingVccService.setBraintreeMatchingFilter(this.config);

    if (this.dialogRef) {
      this.dialogRef.close(this.config);
    }
  }

  closeForm() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  clearMatchingFields(...args: string[]) {
    if (!args.length) {
      this.braintreeMatchingFilterForm.reset();
      return;
    }
    args.forEach((field) => {
      this.braintreeMatchingFilterForm.controls[field].reset();
    });
  }

  checkInput(event: KeyboardEvent): void {
    const charCode = event.charCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
}
